import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Button } from '@/kit/Button';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { EmptyRow } from '@/features/EmptyRow';
import { InputField } from '@/kit/FormField';
import { Icon } from '@/kit/Icon';
import { RequiredMark } from '@/kit/RequiredMark';
import { Select } from '@/kit/Select';
import { Tooltip } from '@/kit/Tooltip';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { periodRangeText } from '@/core/format/periodLabel';
import { dateRangeProblem } from '@/core/utils/dates';
import { validateForm } from '@/features/manage/formRules';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { PERIOD_STATUS_LABELS, PeriodStatus } from '@/core/models/approval.models';
import { selectedClient } from '@/core/selectedClient';
import { groupService } from '@/core/services/group';
import { practiceService } from '@/core/services/practice';
import { toIsoDate, type PeriodRow, type SideListItem } from '@/core/models/practice.models';
import {
  GroupEventType,
  GroupInclusionStatus,
  GroupStatus,
  GroupUndertakingType,
  type GroupEventRow,
  type GroupStructureView,
  type GroupSummaryRow,
  type GroupUndertakingRow,
} from '@/core/models/group.models';
import { decimalPipe } from './format';
import { useMounted } from './useMounted';
import './TaxreturnGroupStructure.scss';

const undertakingTypes = [
  { id: GroupUndertakingType.Subsidiary, name: 'Subsidiary' },
  { id: GroupUndertakingType.Associate, name: 'Associate' },
  { id: GroupUndertakingType.JointVenture, name: 'Joint venture' },
  { id: GroupUndertakingType.Subgroup, name: 'Subgroup' },
];
const inclusionStatuses = [
  { id: GroupInclusionStatus.Included, name: 'Consolidated' },
  { id: GroupInclusionStatus.Excluded, name: 'Excluded' },
  { id: GroupInclusionStatus.EquityAccounted, name: 'Equity accounted' },
];
const eventTypes = [
  { id: GroupEventType.Acquisition, name: 'Acquisition' },
  { id: GroupEventType.Disposal, name: 'Disposal' },
  { id: GroupEventType.OwnershipChange, name: 'Ownership change' },
];

/** A blank box is null, anything else is parsed (the boxes only let digits and one decimal point in). */
const numberOrNull = (text: string): number | null => (text.trim() === '' ? null : Number(text));

/** The message for a percentage box that is blank or outside 0 to 100 (the database rejects anything else), else null. */
function percentProblem(text: string, what: string): string | null {
  const value = numberOrNull(text);
  return value === null || !Number.isFinite(value) || value < 0 || value > 100
    ? `${what} must be a number from 0 to 100.`
    : null;
}

/** Longest text each column takes (db/schema). */
const EXCLUSION_REASON_MAX = 400;
const DISCLOSURE_MAX = 2000;
const REVISION_REASON_MAX = 500;
const GROUP_NAME_MAX = 255;

function periodRange(item: PeriodRow): ReactNode {
  return periodRangeText(item?.periodFrom, item?.periodTo, ' to ');
}

export function TaxreturnGroupStructure() {
  const mounted = useMounted();

  const [groups, setGroups] = useState<GroupSummaryRow[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [structure, setStructure] = useState<GroupStructureView | null>(null);

  const [clients, setClients] = useState<SideListItem[]>([]);
  /** Accounting periods of the client currently chosen in the undertaking form. */
  const [undertakingPeriods, setUndertakingPeriods] = useState<PeriodRow[]>([]);
  const [parentPeriods, setParentPeriods] = useState<PeriodRow[]>([]);

  // Create-group form
  const [isCreating, setIsCreating] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newParentClientId, setNewParentClientId] = useState<string | null>(() => selectedClient()?.id ?? null);
  const [newParentPeriodId, setNewParentPeriodId] = useState<string | null>(null);
  const [newStartDate, setNewStartDate] = useState('');
  const [newEndDate, setNewEndDate] = useState('');

  // Undertaking form
  const [isEditingUndertaking, setIsEditingUndertaking] = useState(false);
  const [editingUndertakingId, setEditingUndertakingId] = useState<string | null>(null);
  const [formClientId, setFormClientId] = useState<string | null>(null);
  const [formPeriodId, setFormPeriodId] = useState<string | null>(null);
  const [formParentUndertakingId, setFormParentUndertakingId] = useState<string | null>(null);
  const [formType, setFormType] = useState<GroupUndertakingType>(GroupUndertakingType.Subsidiary);
  const [formOwnership, setFormOwnership] = useState('100');
  const [formEffectiveFrom, setFormEffectiveFrom] = useState('');
  const [formEffectiveTo, setFormEffectiveTo] = useState<string | null>(null);
  const [formInclusion, setFormInclusion] = useState<GroupInclusionStatus>(GroupInclusionStatus.Included);
  const [formExclusionReason, setFormExclusionReason] = useState('');
  const [formRowVersion, setFormRowVersion] = useState<string | null>(null);

  // Acquisition/disposal form
  const [isEditingEvent, setIsEditingEvent] = useState(false);
  const [eventUndertakingId, setEventUndertakingId] = useState<string | null>(null);
  const [eventType, setEventType] = useState<GroupEventType>(GroupEventType.Acquisition);
  const [eventDate, setEventDate] = useState('');
  const [eventBefore, setEventBefore] = useState('0');
  const [eventAfter, setEventAfter] = useState('100');
  const [eventConsideration, setEventConsideration] = useState('');
  const [eventDisclosure, setEventDisclosure] = useState('');

  /** The `#revisionReason` input is read on click, never bound. */
  const revisionReason = useRef<HTMLInputElement>(null);

  // A failed save marks the missing fields red; each flag clears when its form is opened again.
  const [createTried, setCreateTried] = useState(false);
  const [undertakingTried, setUndertakingTried] = useState(false);
  const [eventTried, setEventTried] = useState(false);
  const [revisionMissing, setRevisionMissing] = useState(false);
  // In-flight flags: a second click on the same button waits for the first request.
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [savingUndertaking, setSavingUndertaking] = useState(false);
  const [savingEvent, setSavingEvent] = useState(false);
  const [working, setWorking] = useState(false);

  const loadParentPeriods = useCallback(
    (clientId: string) => {
      practiceService
        .getClientPeriods(clientId)
        .then((response) => {
          if (mounted.current) setParentPeriods(response.periods);
        })
        .catch(() => {
          if (mounted.current) setParentPeriods([]);
        });
    },
    [mounted],
  );

  const loadUndertakingPeriods = useCallback(
    (clientId: string) => {
      practiceService
        .getClientPeriods(clientId)
        .then((response) => {
          if (mounted.current) setUndertakingPeriods(response.periods);
        })
        .catch(() => {
          if (mounted.current) setUndertakingPeriods([]);
        });
    },
    [mounted],
  );

  const load = useCallback(
    (groupId: string | null) => {
      if (!groupId) return;
      groupService
        .getGroup(groupId)
        .then((result) => {
          if (mounted.current) setStructure(result);
        })
        .catch((error) => {
          if (mounted.current) notifyError(error);
        });
    },
    [mounted],
  );

  const refreshGroupList = () => {
    const client = selectedClient();
    groupService
      .getGroups(client?.id ?? null)
      .then((result) => {
        if (mounted.current) setGroups(result);
      })
      .catch(() => undefined);
  };

  useEffect(() => {
    // loadClients
    practiceService
      .getClientSideList(1, 500, '')
      .then((response) => {
        if (mounted.current) setClients(response.sideModuleList);
      })
      .catch((error) => {
        if (mounted.current) notifyError(error);
      });

    // loadGroups
    const client = selectedClient();
    if (client?.id) {
      loadParentPeriods(client.id);
    }
    groupService
      .getGroups(client?.id ?? null)
      .then((result) => {
        if (!mounted.current) return;
        setGroups(result);
        const first = result.length > 0 ? result[0].groupPeriodId : null;
        setSelectedGroupId(first);
        if (first) {
          load(first);
        } else {
          setStructure(null);
        }
      })
      .catch((error) => {
        if (mounted.current) notifyError(error);
      });
    // Runs once, like ngOnInit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Create a group ----

  const startCreate = () => {
    setIsCreating(true);
    setCreateTried(false);
    setNewGroupName('');
    setNewParentPeriodId(null);
  };

  const onParentClientChange = (clientId: string | null) => {
    setNewParentClientId(clientId);
    setNewParentPeriodId(null);
    setParentPeriods([]);
    if (clientId) {
      loadParentPeriods(clientId);
    }
  };

  /** The group period must match the parent's accounting period, so its dates follow it. */
  const onParentPeriodChange = (periodId: string | null) => {
    setNewParentPeriodId(periodId);
    const period = parentPeriods.find((p) => p.id === periodId);
    if (period) {
      setNewStartDate(toIsoDate(period.periodFrom) ?? '');
      setNewEndDate(toIsoDate(period.periodTo) ?? '');
    }
  };

  const createGroup = () => {
    // The API needs the group name as well as the parent company and period.
    if (!newGroupName.trim() || !newParentClientId || !newParentPeriodId) {
      setCreateTried(true);
      validateForm(false);
      return;
    }
    setCreatingGroup(true);
    groupService
      .createGroup({
        parentClientId: newParentClientId,
        parentAccountingPeriodId: newParentPeriodId,
        groupName: newGroupName.trim(),
        startDate: newStartDate,
        endDate: newEndDate,
      })
      .then((result) => {
        if (!mounted.current) return;
        setIsCreating(false);
        notifySuccess('The group has been created.');
        setSelectedGroupId(result.group.groupPeriodId);
        setStructure(result);
        refreshGroupList();
      })
      .catch((error) => {
        if (mounted.current) notifyError(error);
      })
      .finally(() => {
        if (mounted.current) setCreatingGroup(false);
      });
  };

  // ---- Undertakings ----

  const newUndertaking = () => {
    setIsEditingUndertaking(true);
    setUndertakingTried(false);
    setEditingUndertakingId(null);
    setFormClientId(null);
    setFormPeriodId(null);
    setFormParentUndertakingId(null);
    setFormType(GroupUndertakingType.Subsidiary);
    setFormOwnership('100');
    setFormEffectiveFrom(structure ? structure.group.startDate : '');
    setFormEffectiveTo(null);
    setFormInclusion(GroupInclusionStatus.Included);
    setFormExclusionReason('');
    setFormRowVersion(null);
    setUndertakingPeriods([]);
  };

  const editUndertaking = (row: GroupUndertakingRow) => {
    setIsEditingUndertaking(true);
    setUndertakingTried(false);
    setEditingUndertakingId(row.groupUndertakingId);
    setFormClientId(row.clientId);
    setFormPeriodId(row.accountingPeriodId);
    setFormParentUndertakingId(row.parentUndertakingId);
    setFormType(row.undertakingType);
    setFormOwnership(String(row.ownershipPercent));
    setFormEffectiveFrom(row.effectiveFrom);
    setFormEffectiveTo(row.effectiveTo);
    setFormInclusion(row.inclusionStatus);
    setFormExclusionReason(row.exclusionReason ?? '');
    setFormRowVersion(row.rowVersion);
    loadUndertakingPeriods(row.clientId);
  };

  const onUndertakingClientChange = (clientId: string | null) => {
    setFormClientId(clientId);
    setFormPeriodId(null);
    setUndertakingPeriods([]);
    if (clientId) {
      loadUndertakingPeriods(clientId);
    }
  };

  /**
   * #153: "effective from" now defaults to the later of the group start and the chosen period's start (it can still
   * be edited).
   */
  const onUndertakingPeriodChange = (periodId: string | null) => {
    setFormPeriodId(periodId);
    const period = undertakingPeriods.find((p) => p.id === periodId);
    const periodStart = period ? toIsoDate(period.periodFrom) : null;
    const groupStart = structure?.group.startDate ?? null;
    if (periodStart) {
      setFormEffectiveFrom(groupStart && groupStart > periodStart ? groupStart : periodStart);
    }
  };

  const saveUndertaking = () => {
    if (!selectedGroupId) {
      notifyError('Select a group first.');
      return;
    }
    if (!formClientId || !formEffectiveFrom) {
      setUndertakingTried(true);
      validateForm(false);
      return;
    }
    const problem =
      percentProblem(formOwnership, 'Ownership %') ??
      dateRangeProblem(formEffectiveFrom, formEffectiveTo, 'The effective period') ??
      (formExclusionReason.length > 0 && formExclusionReason.trim() === ''
        ? 'The exclusion reason cannot be only spaces.'
        : null);
    if (problem) {
      setUndertakingTried(true);
      notifyError(problem);
      return;
    }

    setSavingUndertaking(true);
    groupService
      .saveUndertaking({
        groupPeriodId: selectedGroupId,
        groupUndertakingId: editingUndertakingId,
        clientId: formClientId,
        accountingPeriodId: formPeriodId,
        parentUndertakingId: formParentUndertakingId,
        undertakingType: formType,
        ownershipPercent: Number(formOwnership),
        effectiveFrom: formEffectiveFrom,
        effectiveTo: formEffectiveTo,
        inclusionStatus: formInclusion,
        exclusionReason: formExclusionReason.trim() || null,
        sortOrder: 0,
        rowVersion: formRowVersion,
      })
      .then((result) => {
        if (!mounted.current) return;
        setIsEditingUndertaking(false);
        notifySuccess('The group structure has been updated.');
        setStructure(result);
      })
      .catch((error) => {
        if (mounted.current) notifyError(error);
      })
      .finally(() => {
        if (mounted.current) setSavingUndertaking(false);
      });
  };

  const deleteUndertaking = (row: GroupUndertakingRow) => {
    if (!selectedGroupId) {
      notifyError('Select a group first.');
      return;
    }
    groupService
      .deleteUndertaking(selectedGroupId, row.groupUndertakingId)
      .then((result) => {
        if (!mounted.current) return;
        notifySuccess('The undertaking has been removed.');
        setStructure(result);
      })
      .catch((error) => {
        if (mounted.current) notifyError(error);
      });
  };

  // ---- Acquisitions and disposals ----

  const newEvent = () => {
    setIsEditingEvent(true);
    setEventTried(false);
    setEventUndertakingId(null);
    setEventType(GroupEventType.Acquisition);
    setEventDate(structure ? structure.group.startDate : '');
    setEventBefore('0');
    setEventAfter('100');
    setEventConsideration('');
    setEventDisclosure('');
  };

  const saveEvent = () => {
    if (!selectedGroupId) {
      notifyError('Select a group first.');
      return;
    }
    if (!eventUndertakingId) {
      setEventTried(true);
      validateForm(false);
      return;
    }
    const problem =
      percentProblem(eventBefore, 'Ownership before %') ??
      percentProblem(eventAfter, 'Ownership after %') ??
      (eventDisclosure.length > 0 && eventDisclosure.trim() === '' ? 'The disclosure text cannot be only spaces.' : null);
    if (problem) {
      setEventTried(true);
      notifyError(problem);
      return;
    }

    setSavingEvent(true);
    groupService
      .saveEvent({
        groupPeriodId: selectedGroupId,
        groupUndertakingId: eventUndertakingId,
        eventType,
        eventDate,
        ownershipBefore: Number(eventBefore),
        ownershipAfter: Number(eventAfter),
        consideration: numberOrNull(eventConsideration),
        disclosureText: eventDisclosure.trim() || null,
      })
      .then((result) => {
        if (!mounted.current) return;
        setIsEditingEvent(false);
        notifySuccess('The acquisition or disposal has been recorded.');
        setStructure(result);
      })
      .catch((error) => {
        if (mounted.current) notifyError(error);
      })
      .finally(() => {
        if (mounted.current) setSavingEvent(false);
      });
  };

  const deleteEvent = (groupEventId: string) => {
    if (!selectedGroupId) {
      notifyError('Select a group first.');
      return;
    }
    groupService
      .deleteEvent(selectedGroupId, groupEventId)
      .then((result) => {
        if (mounted.current) setStructure(result);
      })
      .catch((error) => {
        if (mounted.current) notifyError(error);
      });
  };

  // ---- Readiness, finalisation and revision ----

  const run = (request: Promise<GroupStructureView>, message: string) => {
    setWorking(true);
    request
      .then((result) => {
        if (!mounted.current) return;
        notifySuccess(message);
        setStructure(result);
        refreshGroupList();
      })
      .catch((error) => {
        if (mounted.current) notifyError(error);
      })
      .finally(() => {
        if (mounted.current) setWorking(false);
      });
  };

  const setReady = () => {
    if (!selectedGroupId) {
      notifyError('Select a group first.');
      return;
    }
    run(groupService.setReady(selectedGroupId), 'The group is ready for approval.');
  };

  const finalise = () => {
    if (!selectedGroupId) {
      notifyError('Select a group first.');
      return;
    }
    run(groupService.finalise(selectedGroupId), 'The group accounts have been finalised.');
  };

  const unfinalise = () => {
    if (!selectedGroupId) {
      notifyError('Select a group first.');
      return;
    }
    run(groupService.unfinalise({ groupPeriodId: selectedGroupId }), 'The group accounts have been unfinalised.');
  };

  const revise = (reason: string) => {
    if (!selectedGroupId) {
      notifyError('Select a group first.');
      return;
    }
    // The API needs a reason; say so instead of doing nothing.
    if (!reason.trim()) {
      setRevisionMissing(true);
      validateForm(false);
      return;
    }

    setWorking(true);
    groupService
      .createRevision({ groupPeriodId: selectedGroupId, reason: reason.trim() })
      .then((result) => {
        if (!mounted.current) return;
        notifySuccess('A revised set of group accounts has been created.');
        setSelectedGroupId(result.group.groupPeriodId);
        setStructure(result);
        refreshGroupList();
      })
      .catch((error) => {
        if (mounted.current) notifyError(error);
      })
      .finally(() => {
        if (mounted.current) setWorking(false);
      });
  };

  const undertakings = structure ? structure.undertakings : [];

  const undertakingColumns: DataTableColumn<GroupUndertakingRow>[] = [
    { id: 'name', header: 'Company', cell: (row) => row.clientName },
    { id: 'type', header: 'Type', cell: (row) => row.undertakingTypeLabel },
    {
      id: 'ownership',
      header: 'Ownership',
      headerClassName: 'text-right',
      cellClassName: 'text-right',
      cell: (row) => `${decimalPipe(row.ownershipPercent)}%`,
    },
    {
      id: 'period',
      header: 'Accounting period',
      cell: (row) => (
        <>
          {row.periodLabel}{' '}
          {row.accountingPeriodId && !row.isPeriodCompatible ? (
            <span className="text-danger">(does not overlap the group period)</span>
          ) : null}
        </>
      ),
    },
    {
      // task-152: readiness turns on the accounting period's own status, not on whether a trial
      // balance exists, so the table shows it. A period below Ready for review flags the
      // undertaking however good its trial balance is, and nothing said which it was.
      id: 'periodStatus',
      header: 'Period status',
      cell: (row) =>
        row.periodStatus === null ? (
          ''
        ) : (
          <span className={row.periodStatus < PeriodStatus.ReadyForReview ? 'text-danger' : ''}>
            {PERIOD_STATUS_LABELS[row.periodStatus] ?? row.periodStatus}
          </span>
        ),
    },
    {
      id: 'effective',
      header: 'Effective',
      cell: (row) => `${row.effectiveFrom}${row.effectiveTo ? ' to ' + row.effectiveTo : ''}`,
    },
    { id: 'inclusion', header: 'Consolidation', cell: (row) => row.inclusionStatusLabel },
    {
      id: 'ready',
      header: 'Ready',
      cell: (row) => <Icon name={row.isReady ? 'check_circle' : 'error_outline'} outlined />,
    },
    {
      id: 'action',
      header: 'Action',
      cell: (row) => (
        <>
          <Tooltip title="Edit">
            <Button onClick={() => editUndertaking(row)}>
              <Icon name="edit" outlined />
            </Button>
          </Tooltip>
          {row.undertakingType !== 0 && (
            <Tooltip title="Remove">
              <Button onClick={() => deleteUndertaking(row)}>
                <Icon name="delete_outline" outlined />
              </Button>
            </Tooltip>
          )}
        </>
      ),
    },
  ];

  const eventColumns: DataTableColumn<GroupEventRow>[] = [
    { id: 'undertaking', header: 'Company', cell: (row) => row.clientName },
    { id: 'type', header: 'Event', cell: (row) => row.eventTypeLabel },
    { id: 'date', header: 'Date', cell: (row) => row.eventDate },
    {
      id: 'before',
      header: 'Before',
      headerClassName: 'text-right',
      cellClassName: 'text-right',
      cell: (row) => `${decimalPipe(row.ownershipBefore)}%`,
    },
    {
      id: 'after',
      header: 'After',
      headerClassName: 'text-right',
      cellClassName: 'text-right',
      cell: (row) => `${decimalPipe(row.ownershipAfter)}%`,
    },
    {
      id: 'consideration',
      header: 'Consideration',
      headerClassName: 'text-right',
      cellClassName: 'text-right',
      cell: (row) => decimalPipe(row.consideration),
    },
    {
      id: 'action',
      header: 'Action',
      cell: (row) => (
        <Tooltip title="Delete">
          <Button onClick={() => deleteEvent(row.groupEventId)}>
            <Icon name="delete_outline" outlined />
          </Button>
        </Tooltip>
      ),
    },
  ];

  return (
    <SideMenuPage menu="task" contentClassName="taxreturn-group-structure">
      <div className="content-body">
        <div className="content-header mb-0">
          <div className="content-header-left mb-1">
            <Button className="action-button primary-button" onClick={startCreate}>
              <Icon name="add" />
              <span>Group</span>
            </Button>
            <Select<GroupSummaryRow>
              className="w-300 ml-1"
              openOnFocus
              panelClass="myPanelClass"
              items={groups}
              bindLabel="groupName"
              bindValue="groupPeriodId"
              clearable={false}
              value={selectedGroupId}
              onChange={(value) => {
                const groupId = value as string | null;
                setSelectedGroupId(groupId);
                load(groupId);
              }}
            />
          </div>
          {/* Same counts-at-a-glance treatment as Statutory Disclosures' "n unanswered / required / optional /
              not applicable" row: the one thing blocking Mark ready (in red, only while there is one) first,
              then a plain breakdown of the undertakings table below. */}
          {structure && (
            <div className="content-header-right mb-1 d-flex align-items-center gap-15">
              {structure.readinessIssues.length > 0 && (
                <span className="text-red fw-bold">
                  {structure.readinessIssues.length} {structure.readinessIssues.length === 1 ? 'issue' : 'issues'}
                </span>
              )}
              <span className="text-gray">
                {structure.undertakings.length} {structure.undertakings.length === 1 ? 'undertaking' : 'undertakings'}
              </span>
              <span className="text-gray">
                {structure.undertakings.filter((u) => u.inclusionStatus === GroupInclusionStatus.Included).length}{' '}
                consolidated
              </span>
              <span className="text-gray">{structure.undertakings.filter((u) => !u.isReady).length} not ready</span>
            </div>
          )}
        </div>

        {isCreating && (
          <div className="edit-panel mb-2">
            <h3>New group</h3>
            <div className="d-flex flex-wrap">
              <span className="req-field">
                <InputField
                  fieldClassName="w-300"
                  placeholder="Group name"
                  charMode="alphanumeric"
                  maxLength={GROUP_NAME_MAX}
                  invalid={createTried && !newGroupName.trim()}
                  value={newGroupName}
                  onChange={(event) => setNewGroupName(event.target.value)}
                  autoComplete="off"
                />
                <RequiredMark />
              </span>
              <span className="req-field">
                <Select<SideListItem>
                  className={`w-250 ml-1${createTried && !newParentClientId ? ' ng-invalid ng-touched' : ''}`}
                  panelClass="myPanelClass"
                  items={clients}
                  bindLabel="name"
                  bindValue="id"
                  placeholder="Parent company"
                  value={newParentClientId}
                  onChange={(value) => onParentClientChange(value as string | null)}
                />
                <RequiredMark />
              </span>
              <span className="req-field">
                <Select<PeriodRow>
                  className={`w-250 ml-1${createTried && !newParentPeriodId ? ' ng-invalid ng-touched' : ''}`}
                  panelClass="myPanelClass"
                  items={parentPeriods}
                  bindLabel="periodFrom"
                  bindValue="id"
                  placeholder="Parent accounting period"
                  value={newParentPeriodId}
                  onChange={(value) => onParentPeriodChange(value as string | null)}
                  renderOption={periodRange}
                />
                <RequiredMark />
              </span>
            </div>
            {createTried && !newGroupName.trim() && <p className="text-danger mb-0">Enter the group name.</p>}
            {createTried && !newParentClientId && <p className="text-danger mb-0">Select the parent company.</p>}
            {createTried && !newParentPeriodId && (
              <p className="text-danger mb-0">Select the parent accounting period.</p>
            )}
            <p className="mb-0">
              Group period: {newStartDate} to {newEndDate} (taken from the parent's accounting period).
            </p>
            <div className="mt-1">
              <Button className="action-button primary-button" busy={creatingGroup} onClick={createGroup}>
                Create
              </Button>
              <Button className="action-button secondary-button ml-1" onClick={() => setIsCreating(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {structure && (
          <div className="mb-1">
            <p className="mb-0">
              <strong>{structure.group.groupName}</strong> — {structure.group.parentName} —{' '}
              {structure.group.periodLabel} — {structure.group.statusLabel}
              {structure.group.revisionNo > 0 && <span> (revision {structure.group.revisionNo})</span>}
            </p>
            {structure.group.isStale && <p className="text-danger mb-0">{structure.group.staleReason}</p>}
            {structure.readinessIssues.length > 0 && (
              <ul className="text-danger">
                {structure.readinessIssues.map((issue, index) => (
                  <li key={index}>{issue}</li>
                ))}
              </ul>
            )}
            {/* Same white, bordered, shadowed bar as the other content-header filter/action rows (e.g. Statutory
                Disclosures' search/state/period row), instead of the button row sitting loose on the page. */}
            <div className="content-header group-actions-bar mb-0 mt-1">
              <div className="content-header-left mb-1">
                <Button
                  className="action-button primary-button"
                  disabled={structure.readinessIssues.length > 0 || structure.group.status !== GroupStatus.Draft}
                  busy={working}
                  onClick={setReady}
                >
                  Mark ready
                </Button>
                <Button
                  className="action-button primary-button ml-1"
                  disabled={structure.group.status !== GroupStatus.Ready}
                  busy={working}
                  onClick={finalise}
                >
                  Finalise
                </Button>
                <Button
                  className="action-button secondary-button ml-1"
                  disabled={structure.group.status !== GroupStatus.Finalised}
                  busy={working}
                  onClick={unfinalise}
                >
                  Unfinalise
                </Button>
                <Button
                  className="action-button secondary-button ml-1"
                  disabled={structure.group.status !== GroupStatus.Finalised}
                  busy={working}
                  onClick={() => revise(revisionReason.current?.value ?? '')}
                >
                  Create revision
                </Button>
                <input
                  ref={revisionReason}
                  className={`mat-mdc-input-element group-revision-reason ml-1${revisionMissing ? ' group-revision-reason--invalid' : ''}`}
                  placeholder="Reason for revision"
                  maxLength={REVISION_REASON_MAX}
                  aria-invalid={revisionMissing || undefined}
                  onChange={() => setRevisionMissing(false)}
                  autoComplete="off"
                />
                <RequiredMark />
              </div>
            </div>
            {revisionMissing && <p className="text-danger mb-0">Enter the reason for the revision.</p>}
          </div>
        )}

        <h3>Undertakings</h3>
        <Button className="action-button primary-button mb-1" disabled={!selectedGroupId} onClick={newUndertaking}>
          <Icon name="add" />
          <span>Undertaking</span>
        </Button>

        {isEditingUndertaking && (
          <div className="edit-panel mb-2">
            <div className="d-flex flex-wrap">
              <span className="req-field">
                <Select<SideListItem>
                  className={`w-250${undertakingTried && !formClientId ? ' ng-invalid ng-touched' : ''}`}
                  panelClass="myPanelClass"
                  items={clients}
                  bindLabel="name"
                  bindValue="id"
                  placeholder="Company"
                  value={formClientId}
                  onChange={(value) => onUndertakingClientChange(value as string | null)}
                />
                <RequiredMark />
              </span>
              <Select<PeriodRow>
                className="w-250 ml-1"
                panelClass="myPanelClass"
                items={undertakingPeriods}
                bindLabel="periodFrom"
                bindValue="id"
                placeholder="Accounting period"
                value={formPeriodId}
                onChange={(value) => onUndertakingPeriodChange(value as string | null)}
                renderLabel={periodRange}
                renderOption={periodRange}
              />
              <Select
                className="w-200 ml-1"
                panelClass="myPanelClass"
                items={undertakingTypes}
                bindLabel="name"
                bindValue="id"
                clearable={false}
                value={formType}
                onChange={(value) => setFormType(value as GroupUndertakingType)}
              />
              <Select<GroupUndertakingRow>
                className="w-200 ml-1"
                panelClass="myPanelClass"
                items={undertakings}
                bindLabel="clientName"
                bindValue="groupUndertakingId"
                placeholder="Held through (subgroup)"
                value={formParentUndertakingId}
                onChange={(value) => setFormParentUndertakingId(value as string | null)}
              />
            </div>
            <div className="d-flex flex-wrap mt-1">
              <InputField
                fieldClassName="w-150"
                charMode="number"
                allowDecimal
                maxDecimals={4}
                maxIntDigits={3}
                inputMode="decimal"
                placeholder="Ownership %"
                invalid={undertakingTried && percentProblem(formOwnership, 'Ownership %') !== null}
                value={formOwnership}
                onChange={(event) => setFormOwnership(event.target.value)}
              />
              <span className="req-field">
                <InputField
                  fieldClassName="w-150 ml-1"
                  type="date"
                  invalid={undertakingTried && !formEffectiveFrom}
                  value={formEffectiveFrom}
                  onChange={(event) => setFormEffectiveFrom(event.target.value)}
                />
                <RequiredMark />
              </span>
              <InputField
                fieldClassName="w-150 ml-1"
                type="date"
                min={formEffectiveFrom || undefined}
                invalid={dateRangeProblem(formEffectiveFrom, formEffectiveTo) !== null}
                value={formEffectiveTo ?? ''}
                onChange={(event) => setFormEffectiveTo(event.target.value || null)}
              />
              <Select
                className="w-200 ml-1"
                panelClass="myPanelClass"
                items={inclusionStatuses}
                bindLabel="name"
                bindValue="id"
                clearable={false}
                value={formInclusion}
                onChange={(value) => setFormInclusion(value as GroupInclusionStatus)}
              />
              <InputField
                fieldClassName="w-300 ml-1"
                placeholder="Exclusion reason"
                maxLength={EXCLUSION_REASON_MAX}
                invalid={formExclusionReason.length > 0 && formExclusionReason.trim() === ''}
                value={formExclusionReason}
                onChange={(event) => setFormExclusionReason(event.target.value)}
                autoComplete="off"
              />
            </div>
            <div className="mt-1">
              <Button className="action-button primary-button" busy={savingUndertaking} onClick={saveUndertaking}>
                Save
              </Button>
              <Button className="action-button secondary-button ml-1" onClick={() => setIsEditingUndertaking(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        <div className="mat-table-wrapper">
          <DataTable
            columns={undertakingColumns}
            rows={undertakings}
            rowKey={(row) => row.groupUndertakingId}
            noDataMessage={
              structure ? (
                <EmptyRow icon="account_tree" title="No undertakings added yet" hint="Use the Undertaking button above to add the first one." />
              ) : undefined
            }
          />
        </div>

        <h3 className="mt-2">Acquisitions and disposals in the period</h3>
        <Button className="action-button primary-button mb-1" disabled={!selectedGroupId} onClick={newEvent}>
          <Icon name="add" />
          <span>Acquisition / disposal</span>
        </Button>

        {isEditingEvent && (
          <div className="edit-panel mb-2">
            <div className="d-flex flex-wrap">
              <span className="req-field">
                <Select<GroupUndertakingRow>
                  className={`w-250${eventTried && !eventUndertakingId ? ' ng-invalid ng-touched' : ''}`}
                  panelClass="myPanelClass"
                  items={undertakings}
                  bindLabel="clientName"
                  bindValue="groupUndertakingId"
                  placeholder="Undertaking"
                  value={eventUndertakingId}
                  onChange={(value) => setEventUndertakingId(value as string | null)}
                />
                <RequiredMark />
              </span>
              <Select
                className="w-200 ml-1"
                panelClass="myPanelClass"
                items={eventTypes}
                bindLabel="name"
                bindValue="id"
                clearable={false}
                value={eventType}
                onChange={(value) => setEventType(value as GroupEventType)}
              />
              <InputField
                fieldClassName="w-150 ml-1"
                type="date"
                value={eventDate}
                onChange={(event) => setEventDate(event.target.value)}
              />
              <InputField
                fieldClassName="w-150 ml-1"
                charMode="number"
                allowDecimal
                maxDecimals={4}
                maxIntDigits={3}
                inputMode="decimal"
                placeholder="Ownership before %"
                invalid={eventTried && percentProblem(eventBefore, 'Ownership before %') !== null}
                value={eventBefore}
                onChange={(event) => setEventBefore(event.target.value)}
              />
              <InputField
                fieldClassName="w-150 ml-1"
                charMode="number"
                allowDecimal
                maxDecimals={4}
                maxIntDigits={3}
                inputMode="decimal"
                placeholder="Ownership after %"
                invalid={eventTried && percentProblem(eventAfter, 'Ownership after %') !== null}
                value={eventAfter}
                onChange={(event) => setEventAfter(event.target.value)}
              />
              <InputField
                fieldClassName="w-150 ml-1"
                charMode="number"
                allowDecimal
                maxIntDigits={15}
                maxDecimals={4}
                inputMode="decimal"
                placeholder="Consideration"
                value={eventConsideration}
                onChange={(event) => setEventConsideration(event.target.value)}
              />
            </div>
            <InputField
              fieldClassName="w-100pc mt-1"
              placeholder="Disclosure text"
              maxLength={DISCLOSURE_MAX}
              invalid={eventDisclosure.length > 0 && eventDisclosure.trim() === ''}
              value={eventDisclosure}
              onChange={(event) => setEventDisclosure(event.target.value)}
              autoComplete="off"
            />
            <div className="mt-1">
              <Button className="action-button primary-button" busy={savingEvent} onClick={saveEvent}>
                Save
              </Button>
              <Button className="action-button secondary-button ml-1" onClick={() => setIsEditingEvent(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        <div className="mat-table-wrapper">
          <DataTable
            columns={eventColumns}
            rows={structure ? structure.events : []}
            rowKey={(row) => row.groupEventId}
            noDataMessage={
              structure ? (
                <EmptyRow icon="event_note" title="No acquisitions or disposals in the period" hint="Use the Acquisition / disposal button above to record one." />
              ) : undefined
            }
          />
        </div>
      </div>
    </SideMenuPage>
  );
}
